import type { ImageDataType } from "@/shared/types";

const MIN_CAROUSEL_LOOP_SLIDES = 4;

export type CarouselSlide = {
  key: string;
  image: ImageDataType;
  realIndex: number;
};

/** Stable infinite loop for Swiper when the gallery has few images. */
export function buildCarouselSlides(images: ImageDataType[]): CarouselSlide[] {
  if (images.length === 0) {
    return [];
  }

  if (images.length === 1) {
    return [{ key: "slide-0", image: images[0], realIndex: 0 }];
  }

  const repeatCount = Math.max(3, Math.ceil(MIN_CAROUSEL_LOOP_SLIDES / images.length));

  return Array.from({ length: repeatCount }, (_, repeatIndex) =>
    images.map((image, imageIndex) => ({
      key: `slide-${repeatIndex}-${imageIndex}`,
      image,
      realIndex: imageIndex,
    }))
  ).flat();
}

export function getCarouselInitialSlide(images: ImageDataType[]): number {
  if (images.length <= 1) {
    return 0;
  }

  return images.length;
}

export function normalizeCarouselIndex(
  activeIndex: number,
  imagesCount: number
): number {
  if (imagesCount <= 0) {
    return 0;
  }

  return ((activeIndex % imagesCount) + imagesCount) % imagesCount;
}

export function shouldResetCarouselPosition(
  activeIndex: number,
  imagesCount: number,
  totalSlides: number
): "backward" | "forward" | null {
  if (imagesCount <= 1 || totalSlides <= imagesCount) {
    return null;
  }

  if (activeIndex < imagesCount) {
    return "backward";
  }

  const lastCopyStart = totalSlides - imagesCount;
  if (activeIndex >= lastCopyStart) {
    return "forward";
  }

  return null;
}
